/**
 * HistoryPage Component (AC-571)
 *
 * Main page for displaying project checkpoints and change history.
 * Uses a two-panel layout: CheckpointList (left 40%) and ChangeList (right 60%).
 *
 * Parent Component: Layout (main nav route /history)
 *
 * Features:
 * - Display "Project History" title with project name
 * - "Create Checkpoint" button opens CheckpointModal
 * - Show loading spinner when checkpointsLoading
 * - Show error message when checkpointsError
 * - Render CheckpointList in left panel
 * - Render ChangeList placeholder in right panel (for AC-572)
 * - Handle missing project context gracefully
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus, ArrowLeft } from 'lucide-react';
import { useContextualNavigation } from '../hooks/useContextualNavigation';
import { useVersionStore } from '../store/versionStore';
import { useProjectStore } from '../store';
import CheckpointList from '../components/history/CheckpointList';
import ChangeList from '../components/history/ChangeList';
import CheckpointModal from '../components/history/CheckpointModal';

const HistoryPage: React.FC = () => {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = projectIdParam ? parseInt(projectIdParam, 10) : null;

  const [isCheckpointModalOpen, setIsCheckpointModalOpen] = useState(false);

  // Contextual back navigation - shows "Back to Workflow" if user came from there
  const { getBackToWorkflow } = useContextualNavigation();
  const backToWorkflow = getBackToWorkflow(projectIdParam);

  const { currentProject } = useProjectStore();
  const {
    checkpoints,
    checkpointsLoading,
    checkpointsError,
    changes,
    changesLoading,
    changesError,
    loadCheckpoints,
    loadChanges,
  } = useVersionStore();

  // Load checkpoints and changes when project ID is available
  useEffect(() => {
    if (projectId) {
      loadCheckpoints(projectId);
      loadChanges(projectId);
    }
  }, [projectId, loadCheckpoints, loadChanges]);

  const handleCheckpointCreated = () => {
    if (projectId) {
      loadCheckpoints(projectId);
    }
    setIsCheckpointModalOpen(false);
  };

  const handleRestoreComplete = () => {
    if (projectId) {
      loadCheckpoints(projectId);
      loadChanges(projectId);
    }
  };

  // Loading state
  if (checkpointsLoading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="ml-3 text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Back navigation */}
      <div className="mb-4">
        {backToWorkflow ? (
          <Link
            to={backToWorkflow.path}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {backToWorkflow.label}
          </Link>
        ) : projectIdParam && (
          <Link
            to={`/projects/${projectIdParam}`}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Overview
          </Link>
        )}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">Project History</h1>
          {currentProject && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{currentProject.name}</p>
          )}
        </div>
        <button
          onClick={() => setIsCheckpointModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors w-full sm:w-auto justify-center min-h-[48px]"
        >
          <Plus className="w-4 h-4" />
          Create Checkpoint
        </button>
      </div>

      {/* Error message */}
      {checkpointsError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{checkpointsError}</p>
        </div>
      )}

      {/* Two-panel layout */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left panel - Checkpoints (40%) */}
        <div className="w-full md:w-2/5">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Checkpoints</h2>
            {projectId && (
              <CheckpointList
                checkpoints={checkpoints}
                projectId={projectId}
                onRestoreComplete={handleRestoreComplete}
                loading={checkpointsLoading}
                error={checkpointsError}
              />
            )}
          </div>
        </div>

        {/* Right panel - Changes (60%) */}
        <div className="w-full md:w-3/5">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Change History</h2>
            {projectId && (
              <ChangeList
                changes={changes}
                projectId={projectId}
                loading={changesLoading}
                error={changesError}
                checkpoints={checkpoints}
              />
            )}
          </div>
        </div>
      </div>

      {/* Checkpoint creation modal */}
      {projectId && (
        <CheckpointModal
          isOpen={isCheckpointModalOpen}
          projectId={projectId}
          onClose={() => setIsCheckpointModalOpen(false)}
          onCheckpointCreated={handleCheckpointCreated}
        />
      )}
    </div>
  );
};

export default HistoryPage;
